import { Link } from 'react-router-dom';
import { MapPin, Maximize2, Home, Building2, Trees, Star } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useSiteData, resolveVisibility } from '@/context/SiteDataContext';
import { useLanguage } from '@/context/LanguageContext';
import { resolveMediaUrl, formatINR } from '@/lib/api';

const statusColors = {
  'Available': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Booked': 'bg-amber-100 text-amber-800 border-amber-200',
  'Sold': 'bg-rose-100 text-rose-800 border-rose-200',
  'Coming Soon': 'bg-sky-100 text-sky-800 border-sky-200',
  'Ready to Move': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Under Construction': 'bg-amber-100 text-amber-800 border-amber-200',
};

const categoryIcons = {
  'Plot': Maximize2,
  'House': Home,
  'Commercial': Building2,
  'Farmhouse': Trees,
};

export const PropertyCard = ({ property }) => {
  const { categories } = useSiteData();
  const { t, lang } = useLanguage();
  const dispTitle = (lang === 'hi' && property.title_hi) ? property.title_hi : property.title;
  const Icon = categoryIcons[property.category] || Building2;
  const visibility = resolveVisibility(property, categories);
  const area = property.plot_area_sqft || property.house_area_sqft || property.builtup_area_sqft || property.carpet_area_sqft;
  const price = property.total_price || (property.rate_per_sqft && area ? property.rate_per_sqft * area : null);
  const showPrice = visibility.show_total_price && Boolean(price);
  const showArea = visibility.show_area && Boolean(area);
  const showRate = visibility.show_rate_per_sqft && Boolean(property.rate_per_sqft);

  return (
    <Link
      to={`/property/${property.id}`}
      className="group block bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
      data-testid={`property-card-${property.code}`}
    >
      <div className="relative h-52 overflow-hidden bg-gradient-to-br from-orange-50 to-amber-50">
        {property.main_image ? (
          <img
            src={resolveMediaUrl(property.main_image)}
            alt={dispTitle}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="h-16 w-16 text-brand-orange/30" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />

        <Badge className={`absolute top-3 left-3 border ${statusColors[property.status] || statusColors['Available']}`}>
          {property.status}
        </Badge>
        <Badge className="absolute top-3 right-3 bg-white/95 text-gray-800 border border-gray-100">
          <Icon className="h-3 w-3 mr-1" />
          {property.category}
        </Badge>
        {property.featured && (
          <Badge className="absolute bottom-3 left-3 bg-amber-400 text-amber-950 border-amber-300">
            <Star className="h-3 w-3 mr-1" fill="currentColor" /> {t('common.featured', 'Featured')}
          </Badge>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between mb-2 gap-2">
          <h3 className="font-heading font-semibold text-gray-900 line-clamp-1 group-hover:text-brand-orange transition-colors">
            {dispTitle}
          </h3>
          <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded shrink-0">
            {property.code}
          </span>
        </div>

        <div className="flex items-center text-sm text-gray-600 mb-3">
          <MapPin className="h-4 w-4 mr-1 text-brand-orange shrink-0" />
          <span className="truncate">{property.locality ? `${property.locality}, ` : ''}{property.location_label}</span>
        </div>

        <div className="flex items-center justify-between text-sm mb-3 gap-2 flex-wrap">
          {showArea && (
            <div className="flex items-center text-gray-600">
              <Maximize2 className="h-4 w-4 mr-1" />
              <span>{Number(area).toLocaleString('en-IN')} sq ft</span>
            </div>
          )}
          {property.bhk && property.bhk !== 'NA' && (
            <div className="flex items-center text-gray-600">
              <Home className="h-4 w-4 mr-1" />
              <span>{property.bhk}</span>
            </div>
          )}
          {property.plot_facing && (
            <span className="text-gray-500 text-xs">{property.plot_facing} facing</span>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 border-t">
          {showPrice ? (
            <div>
              <p className="text-lg font-heading font-bold bg-gradient-to-r from-brand-orange to-brand-rust bg-clip-text text-transparent">
                {formatINR(price)}
              </p>
              {showRate && (
                <p className="text-xs text-gray-500">
                  ₹{Number(property.rate_per_sqft).toLocaleString('en-IN')} / sq ft
                </p>
              )}
            </div>
          ) : (
            <span className="text-brand-orange font-medium text-sm">{t('common.contact_for_details', 'Contact for Details')}</span>
          )}
          <span className="text-brand-orange text-sm font-medium group-hover:translate-x-1 transition-transform inline-flex items-center">
            {t('cta.view_details', 'View Details')} →
          </span>
        </div>

        {property.pricing_note && (
          <p className="text-xs text-brand-rust mt-2 bg-orange-50 px-2 py-1 rounded">
            {property.pricing_note}
          </p>
        )}
      </div>
    </Link>
  );
};
